import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { INotification, NotificationService, NotificationType } from '../../shared/services/notification.service';
import { FriendRequestService } from '../../shared/services/friend-request.service';
// import { ReactionSoundService } from '../../shared/services/reaction-sound.service';
import { CollabrationRequestService } from '../../shared/services/collabration-request.service';

// ─── Icon & colour helpers ─────────────────────────────────────────────────────

function toastIcon(type: NotificationType): string {
  if (type.endsWith('_LIKE'))                                  return '❤️';
  if (type.endsWith('_COMMENT'))                               return '💬';
  if (type.endsWith('_MENTION') || type === 'MENTION')         return '✦';
  if (type.endsWith('_TAG'))                                   return '🏷️';
  if (type === 'FOLLOW')                                       return '➕';
  if (type === 'FRIEND_REQUEST')                               return '👋';
  if (type === 'FRIEND_REQUEST_ACCEPTED')                      return '🤝';
  if (type === 'FRIEND_REQUEST_DECLINED')                      return '✕';
  if (type.endsWith('_COLLABORATION'))                         return '🤝';
  if (type.endsWith('_COLLABORATION_ACCEPTED'))                return '✓';
  if (type.endsWith('_COLLABORATION_DECLINED'))                return '✕';
  return '🔔';
}

function toastAccentColor(type: NotificationType): string {
  if (type.endsWith('_LIKE'))              return '#f43f5e';
  if (type.endsWith('_COMMENT'))           return '#3b82f6';
  if (type.endsWith('_MENTION') || type === 'MENTION') return '#8b5cf6';
  if (type.endsWith('_TAG'))               return '#8b5cf6';
  if (type === 'FOLLOW')                   return '#10b981';
  if (type === 'FRIEND_REQUEST')           return '#D31148';
  if (type === 'FRIEND_REQUEST_ACCEPTED')  return '#10b981';
  if (type.endsWith('_COLLABORATION'))     return '#6366f1';
  return '#6b7280';
}

@Component({
  selector:        'app-notification-toast',
  standalone:      true,
  imports:         [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fixed bottom-5 right-5 z-[9999] flex flex-col gap-3 w-[340px] pointer-events-none">

      <div *ngFor="let toast of toasts; trackBy: trackById"
           class="pointer-events-auto flex items-start gap-3 rounded-2xl border border-[#e8e8e8]
                  bg-white p-4 shadow-[0_4px_24px_rgba(0,0,0,0.10)] animate-toast-in">

        <!-- Avatar + type badge -->
        <div class="relative flex-shrink-0">
          <img *ngIf="avatarUrl(toast); else initials"
               [src]="avatarUrl(toast)"
               [alt]="toast.sender.displayName"
               class="h-10 w-10 rounded-full object-cover border border-[#f0f0f0]" />
          <ng-template #initials>
            <div class="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                 [style.background]="avatarColor(toast.sender.displayName)">
              {{ toast.sender.displayName.charAt(0).toUpperCase() || '?' }}
            </div>
          </ng-template>
          <!-- Type badge dot -->
          <span class="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center
                       justify-center text-[10px] border-2 border-white"
                [style.background]="accentColor(toast.type)">
            {{ icon(toast.type) }}
          </span>
        </div>

        <!-- Body -->
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-gray-800 leading-tight truncate">
            {{ toast.sender.displayName || 'Someone' }}
          </p>
          <div class="mt-0.5 flex items-start gap-2">
            <p class="flex-1 text-xs text-gray-500 line-clamp-2">{{ toast.message }}</p>
            <img *ngIf="thumbnail(toast)" [src]="thumbnail(toast)" alt=""
                 class="h-9 w-9 rounded-lg object-cover border border-[#f0f0f0] flex-shrink-0"/>
          </div>

          <!-- Friend request actions -->
          <div *ngIf="toast.type === 'FRIEND_REQUEST'" class="mt-2 flex flex-wrap gap-2">
            <button (click)="onAccept(toast)" [disabled]="pending[toast._id]"
                    class="rounded-lg bg-[#D31148] px-3 py-1 text-xs font-semibold text-white
                           transition hover:bg-[#b50e3c] disabled:opacity-50">
              Accept
            </button>
            <button (click)="onDecline(toast)" [disabled]="pending[toast._id]"
                    class="rounded-lg border border-[#e8e8e8] bg-[#fafafa] px-3 py-1 text-xs
                           font-semibold text-gray-600 transition hover:border-[#D31148]
                           hover:text-[#D31148] disabled:opacity-50">
              Decline
            </button>
            <button (click)="onDeclineAndBlock(toast)" [disabled]="pending[toast._id]"
                    class="rounded-lg border border-red-200 bg-white px-3 py-1 text-xs
                           font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50">
              Decline &amp; Block
            </button>
          </div>

          <!-- Collaboration actions (post / event / blog) -->
          <div *ngIf="isCollabInvite(toast.type)" class="mt-2 flex gap-2">
            <button (click)="onAcceptCollaboration(toast)" [disabled]="pending[toast._id]"
                    class="rounded-lg bg-[#D31148] px-3 py-1 text-xs font-semibold text-white
                           transition hover:bg-[#b50e3c] disabled:opacity-50">
              Accept
            </button>
            <button (click)="onDeclineCollaboration(toast)" [disabled]="pending[toast._id]"
                    class="rounded-lg border border-[#e8e8e8] bg-[#fafafa] px-3 py-1 text-xs
                           font-semibold text-gray-600 transition hover:border-[#D31148]
                           hover:text-[#D31148] disabled:opacity-50">
              Decline
            </button>
          </div>

          <!-- View link for content notifications -->
          <button *ngIf="hasContentLink(toast.type)"
                  (click)="navigateTo(toast)"
                  class="mt-1.5 text-xs font-semibold text-[#D31148] hover:underline">
            View →
          </button>
        </div>

        <!-- Dismiss -->
        <button (click)="notifService.dismissToast(toast._id)"
                class="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors mt-0.5">
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"
               stroke-width="2.5" stroke-linecap="round">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>
  `,
  styles: [`
    @keyframes toastIn {
      from { opacity: 0; transform: translateX(110%); }
      to   { opacity: 1; transform: translateX(0); }
    }
    .animate-toast-in {
      animation: toastIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }
  `],
})
export class NotificationToastComponent implements OnInit, OnDestroy {

  private notificationService = inject(NotificationService);
  private friendService       = inject(FriendRequestService);
  private collabService       = inject(CollabrationRequestService);
  // private soundService        = inject(ReactionSoundService);
  private router              = inject(Router);
  private cdr                 = inject(ChangeDetectorRef);

  notifService = this.notificationService;

  toasts:  INotification[]          = [];
  pending: Record<string, boolean>  = {};

  private destroy$ = new Subject<void>();

  private readonly AVATAR_COLORS = [
    '#D31148', '#6366f1', '#8b5cf6', '#f97316',
    '#10b981', '#3b82f6', '#f43f5e', '#14b8a6',
  ];

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.notificationService.toasts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(toasts => {
        // if (toasts.length > this.toasts.length) {
        //   this.soundService.playNotification();
        // }
        this.toasts = toasts;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Friend request actions ─────────────────────────────────────────────────

  onAccept(toast: INotification): void {
    const requestId = toast.data?.['friendRequestId'] as string;
    if (!requestId || this.pending[toast._id]) return;

    this.pending[toast._id] = true;
    this.cdr.markForCheck();

    this.friendService.accept(requestId, (toast.sender as any)?._id).subscribe({
      next: () => {
        delete this.pending[toast._id];
        this.notificationService.dismissToast(toast._id);
        this.notificationService.markLocalRead(toast._id);
        this.cdr.markForCheck();
      },
      error: () => { delete this.pending[toast._id]; this.cdr.markForCheck(); },
    });
  }

  onDecline(toast: INotification): void {
    const requestId = toast.data?.['friendRequestId'] as string;
    if (!requestId || this.pending[toast._id]) return;

    this.pending[toast._id] = true;
    this.cdr.markForCheck();

    this.friendService.decline(requestId, (toast.sender as any)?._id).subscribe({
      next: () => {
        delete this.pending[toast._id];
        this.notificationService.dismissToast(toast._id);
        this.notificationService.markLocalRead(toast._id);
        this.cdr.markForCheck();
      },
      error: () => { delete this.pending[toast._id]; this.cdr.markForCheck(); },
    });
  }

  onDeclineAndBlock(toast: INotification): void {
    const requestId = toast.data?.['friendRequestId'] as string;
    if (!requestId || this.pending[toast._id]) return;

    this.pending[toast._id] = true;
    this.cdr.markForCheck();

    this.friendService.declineAndBlock(requestId).subscribe({
      next: () => {
        delete this.pending[toast._id];
        this.notificationService.dismissToast(toast._id);
        this.notificationService.markLocalRead(toast._id);
        this.cdr.markForCheck();
      },
      error: () => { delete this.pending[toast._id]; this.cdr.markForCheck(); },
    });
  }

  // ── Collaboration actions ──────────────────────────────────────────────────

  onAcceptCollaboration(toast: INotification): void   { this.handleCollab(toast, 'accept');  }
  onDeclineCollaboration(toast: INotification): void  { this.handleCollab(toast, 'decline'); }

  private handleCollab(toast: INotification, action: 'accept' | 'decline'): void {
    if (this.pending[toast._id]) return;
    const apiCall = this.collabApiCall(toast, action);
    if (!apiCall) return;

    this.pending[toast._id] = true;
    this.cdr.markForCheck();

    apiCall.subscribe({
      next: () => {
        delete this.pending[toast._id];
        this.notificationService.dismissToast(toast._id);
        this.notificationService.markLocalRead(toast._id);
        this.cdr.markForCheck();
      },
      error: () => { delete this.pending[toast._id]; this.cdr.markForCheck(); },
    });
  }

  private collabApiCall(
    toast: INotification,
    action: 'accept' | 'decline',
  ): Observable<any> | null {
    const type = toast.type as string;

    // Content id stored in notification data
    const contentId = (
      toast.data?.['postId'] ??
      toast.data?.['eventId'] ??
      toast.data?.['blogId'] ??
      toast.data?.['contentId']
    ) as string | undefined;

    if (!contentId) return null;

    if (type === 'POST_COLLABORATION') {
      return action === 'accept'
        ? this.collabService.postCollabrationAccept(contentId)
        : this.collabService.postCollabrationDecline(contentId);
    }
    if (type === 'EVENT_COLLABORATION') {
      return action === 'accept'
        ? this.collabService.eventCollabrationAccept(contentId)
        : this.collabService.eventCollabrationDecline(contentId);
    }
    if (type === 'BLOG_COLLABORATION') {
      return action === 'accept'
        ? this.collabService.blogCollabrationAccept(contentId)
        : this.collabService.blogCollabrationDecline(contentId);
    }

    return null;
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  navigateTo(toast: INotification): void {
    const d    = toast.data ?? {};
    const type = toast.type;

    const contentId = (
      d['contentId'] ?? d['postId'] ?? d['blogId'] ?? d['eventId']
    ) as string | undefined;

    if (contentId) {
      if (type.startsWith('POST'))  { this.router.navigate(['/post',  contentId]); }
      else if (type.startsWith('BLOG'))  { this.router.navigate(['/blog',  contentId]); }
      else if (type.startsWith('EVENT')) { this.router.navigate(['/event', contentId]); }
    } else if (type === 'FOLLOW' || type === 'FRIEND_REQUEST_ACCEPTED') {
      this.router.navigate(['/profile', toast.sender.userName, 'post']);
    }

    this.notificationService.dismissToast(toast._id);
    this.notificationService.markLocalRead(toast._id);
  }

  // ── Template helpers ───────────────────────────────────────────────────────

  icon(type: NotificationType): string        { return toastIcon(type); }
  accentColor(type: NotificationType): string { return toastAccentColor(type); }

  isCollabInvite(type: NotificationType): boolean {
    return type === 'POST_COLLABORATION' ||
           type === 'EVENT_COLLABORATION' ||
           type === 'BLOG_COLLABORATION';
  }

  hasContentLink(type: NotificationType): boolean {
    return (
      type.endsWith('_LIKE')     ||
      type.endsWith('_COMMENT')  ||
      type.endsWith('_MENTION')  ||
      type.endsWith('_TAG')      ||
      type === 'MENTION'         ||
      type.endsWith('_COLLABORATION_ACCEPTED') ||
      type.endsWith('_COLLABORATION_DECLINED')
    );
  }

  trackById(_: number, n: INotification): string { return n._id; }

  avatarUrl(toast: INotification): string | null {
    return toast.sender?.profileImage?.urlSmall ?? null;
  }

  thumbnail(toast: INotification): string | null {
    return (toast.data?.['thumbnail'] as string) ?? null;
  }

  avatarColor(name = ''): string {
    return this.AVATAR_COLORS[(name.charCodeAt(0) || 0) % this.AVATAR_COLORS.length];
  }
}
